import { RefObject, useCallback, useLayoutEffect, useRef } from 'react';

type Subscription = () => void;

interface UseTableResizeHookProps {
    tableRef: RefObject<HTMLTableElement>;
    fontLoaded: boolean;
}

interface UseTableResizeHookReturn {
    subscribeResizeHandler: (sub: Subscription, index?: number) => void;
    unsubscribeResizeHandler: (sub: Subscription) => void;
}

interface UseTableResizeHook {
    (props: UseTableResizeHookProps): UseTableResizeHookReturn;
}

export const useTableResize: UseTableResizeHook = ({ fontLoaded, tableRef }) => {
    const handlers = useRef<Subscription[]>([]);
    const prevWidth = useRef<number | null>(null);

    const subscribeResizeHandler = useCallback((sub: Subscription, index?: number) => {
        if (index) {
            handlers.current.splice(index, 0, sub);
        } else {
            handlers.current.push(sub);
        }
    }, []);

    const unsubscribeResizeHandler = useCallback((sub: Subscription) => {
        handlers.current = handlers.current.filter((handler) => handler !== sub);
    }, []);

    useLayoutEffect(() => {
        if (fontLoaded && tableRef.current) {
            const observer = new ResizeObserver((entries) => {
                const entry = entries[0];
                if (entry.borderBoxSize[0].inlineSize !== prevWidth.current) {
                    handlers.current.forEach((handler) => handler());
                    prevWidth.current = entry.borderBoxSize[0].inlineSize;
                }
            });
            observer.observe(tableRef.current);

            return () => {
                observer.disconnect();
            };
        }
        return void 0;
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    }, [fontLoaded, tableRef.current]);

    return { subscribeResizeHandler, unsubscribeResizeHandler };
};
